import axios from "axios";
import { sendFile } from "./utils";
import { PortalStage, PriorityId, ResolutionId, TicketStatusId, TicketTypeId } from "./tickets";
import { UserId } from "./users";
import { ProjectId } from "./projects";
import { SlaMetric } from "./generated/enums";
import { SlaPolicyId } from "./slaPolicies";

export type PortalId = string & { __brand: "PortalId" };
export type CompanyId = string & { __brand: "CompanyId" };

export { PortalRegistrationMode } from "./generated/enums";
import { PortalRegistrationMode } from "./generated/enums";

export type PortalSummary = {
    id: PortalId;
    name: string;
    slug: string;
    registrationMode: PortalRegistrationMode;
    projectCount: number;
    companyCount: number;
};

export type PortalProjectBinding = {
    projectId: ProjectId;
    openToAllMembers: boolean;
    supportLocalPart: string | null;
};

export type PortalDetail = {
    id: PortalId;
    name: string;
    slug: string;
    registrationMode: PortalRegistrationMode;
    accentColor: string | null;
    introText: string | null;
    hasLogo: boolean;
    projects: PortalProjectBinding[];
    companies: CompanySummary[];
    // Null when the instance polls no inbound mailbox, which makes support addresses read-only.
    inboundPrefix: string | null;
    inboundDomain: string | null;
};

export type CompanySummary = {
    id: CompanyId;
    name: string;
    isBlocked: boolean;
    contactCount: number;
    requestCount: number;
};

export type CompanyContact = {
    userId: UserId;
    name: string;
    email: string;
    isCompanyAdmin: boolean;
    pending: boolean;
    isDisabled: boolean;
    isBlocked: boolean;
};

export type CompanyDetail = {
    id: CompanyId;
    name: string;
    portalId: PortalId;
    isBlocked: boolean;
    membersSeeAllRequests: boolean;
    emailDomains: string[];
    contacts: CompanyContact[];
    requestCount: number;
};

export enum PortalAssignmentStrategy {
    Unassigned = 0,
    RoundRobin = 1,
    LeastBusy = 2,
}

export type ProjectPortalSettings = {
    ticketTypeId: TicketTypeId;
    entryStatusId: TicketStatusId;
    priorityId: PriorityId;
    customersCanResolve: boolean;
    customersSetPriority: boolean;
    allowReopen: boolean;
    autoCloseDays: number | null;
    closeResolutionId: ResolutionId | null;
    assignmentStrategy: PortalAssignmentStrategy;
    /** Empty means everyone in the project who can be assigned tickets. */
    queueMemberIds: UserId[];
    /** Null promises nothing; requests are still timed for reports. */
    slaPolicyId: SlaPolicyId | null;
};

export type CsatTechScore = {
    /** Null marks the "no responsibility recorded" bucket. */
    userId: UserId | null;
    answered: number;
    average: number;
};

export type CsatVerbatim = {
    ticketId: string;
    key: string;
    title: string;
    score: number;
    comment: string;
    respondedAt: string | null;
};

export type CsatReport = {
    sent: number;
    answered: number;
    average: number | null;
    /** Answered counts per score, index 0 = score 1. */
    distribution: number[];
    techs: CsatTechScore[];
    verbatims: CsatVerbatim[];
};

export type SupportWaitingRequest = {
    ticketId: string;
    key: string;
    title: string;
    waitingHours: number;
};

export type SupportMetricSummary = {
    metric: SlaMetric;
    /** Requests that reached the milestone. */
    count: number;
    wallMedianHours: number | null;
    wallP90Hours: number | null;
    businessMedianHours: number | null;
    businessP90Hours: number | null;
    /** Requests with a target for the milestone whose outcome is decided. */
    measured: number;
    met: number;
    attainment: number | null;
};

export type SupportBreakdownCell = {
    metric: SlaMetric;
    count: number;
    wallMedianHours: number | null;
    businessMedianHours: number | null;
};

export type SupportBreakdownRow = {
    name: string;
    requests: number;
    breaches: number;
    cells: SupportBreakdownCell[];
};

export type SupportBreakdowns = {
    customer: SupportBreakdownRow[];
    priority: SupportBreakdownRow[];
    channel: SupportBreakdownRow[];
    project: SupportBreakdownRow[];
};

export type SupportReport = {
    days: number;
    created: number;
    resolved: number;
    breachedOpenCount: number;
    metrics: SupportMetricSummary[];
    breakdowns: SupportBreakdowns;
    waitingCount: number;
    waiting: SupportWaitingRequest[];
};

export type CannedResponseId = string & { __brand: "CannedResponseId" };
export type CannedResponse = {
    id: CannedResponseId;
    name: string;
    body: string;
};

export { PortalStage };

export type CompanyApi = {
    getPortals(): Promise<PortalSummary[]>;
    createPortal(name: string, slug: string): Promise<PortalSummary>;
    getPortal(portal: PortalId): Promise<PortalDetail>;
    editPortal(
        portal: PortalId,
        name: string,
        slug: string,
        registrationMode: PortalRegistrationMode,
        accentColor: string | null,
        introText: string | null
    ): Promise<void>;
    deletePortal(portal: PortalId): Promise<void>;
    uploadPortalLogo(portal: PortalId, file: File): Promise<void>;
    deletePortalLogo(portal: PortalId): Promise<void>;
    getPortalCsat(portal: PortalId, days?: number): Promise<CsatReport>;
    getPortalSupportReport(portal: PortalId, days?: number): Promise<SupportReport>;
    setPortalProjects(portal: PortalId, projects: PortalProjectBinding[]): Promise<void>;
    createCompany(portal: PortalId, name: string): Promise<CompanySummary>;
    getCompany(company: CompanyId): Promise<CompanyDetail>;
    renameCompany(company: CompanyId, name: string): Promise<void>;
    deleteCompany(company: CompanyId): Promise<void>;
    addContact(company: CompanyId, email: string, isCompanyAdmin: boolean): Promise<CompanyContact>;
    editContact(company: CompanyId, user: UserId, isCompanyAdmin: boolean): Promise<void>;
    removeContact(company: CompanyId, user: UserId): Promise<void>;
    setCompanyBlock(company: CompanyId, blocked: boolean): Promise<void>;
    setCompanyMembersSeeAll(company: CompanyId, membersSeeAllRequests: boolean): Promise<void>;
    setContactBlock(company: CompanyId, user: UserId, blocked: boolean): Promise<void>;
    setCompanyDomains(company: CompanyId, domains: string[]): Promise<void>;
    getPortalSettings(project: ProjectId): Promise<ProjectPortalSettings | null>;
    putPortalSettings(
        project: ProjectId,
        settings: ProjectPortalSettings
    ): Promise<ProjectPortalSettings>;
    getCannedResponses(project: ProjectId): Promise<CannedResponse[]>;
    createCannedResponse(project: ProjectId, name: string, body: string): Promise<CannedResponse>;
    updateCannedResponse(
        project: ProjectId,
        response: CannedResponseId,
        name: string,
        body: string
    ): Promise<void>;
    deleteCannedResponse(project: ProjectId, response: CannedResponseId): Promise<void>;
};

export const companyApi: CompanyApi = {
    async getPortals() {
        const { data } = await axios.get<PortalSummary[]>("/api/v0/portals");
        return data;
    },
    async createPortal(name, slug) {
        const { data } = await axios.post<PortalSummary>("/api/v0/portals", { name, slug });
        return data;
    },
    async getPortal(portal) {
        const { data } = await axios.get<PortalDetail>(`/api/v0/portals/${portal}`);
        return data;
    },
    async editPortal(portal, name, slug, registrationMode, accentColor, introText) {
        await axios.put(`/api/v0/portals/${portal}`, {
            name,
            slug,
            registrationMode,
            accentColor,
            introText,
        });
    },
    async deletePortal(portal) {
        await axios.delete(`/api/v0/portals/${portal}`);
    },
    async uploadPortalLogo(portal, file) {
        await sendFile(`/api/v0/portals/${portal}/logo`, file);
    },
    async deletePortalLogo(portal) {
        await axios.delete(`/api/v0/portals/${portal}/logo`);
    },
    async getPortalCsat(portal, days) {
        const { data } = await axios.get<CsatReport>(`/api/v0/portals/${portal}/csat`, {
            params: { days },
        });
        return data;
    },
    async getPortalSupportReport(portal, days) {
        const { data } = await axios.get<SupportReport>(
            `/api/v0/portals/${portal}/support-report`,
            { params: { days } }
        );
        return data;
    },
    async setPortalProjects(portal, projects) {
        await axios.put(`/api/v0/portals/${portal}/projects`, projects);
    },
    async createCompany(portal, name) {
        const { data } = await axios.post<CompanySummary>(`/api/v0/portals/${portal}/companies`, {
            name,
        });
        return { ...data, isBlocked: false, contactCount: 0, requestCount: 0 };
    },
    async getCompany(company) {
        const { data } = await axios.get<CompanyDetail>(`/api/v0/companies/${company}`);
        return data;
    },
    async renameCompany(company, name) {
        await axios.put(`/api/v0/companies/${company}`, { name });
    },
    async deleteCompany(company) {
        await axios.delete(`/api/v0/companies/${company}`);
    },
    async addContact(company, email, isCompanyAdmin) {
        const { data } = await axios.post<CompanyContact>(`/api/v0/companies/${company}/contacts`, {
            email,
            isCompanyAdmin,
        });
        return data;
    },
    async editContact(company, user, isCompanyAdmin) {
        await axios.patch(`/api/v0/companies/${company}/contacts/${user}`, { isCompanyAdmin });
    },
    async removeContact(company, user) {
        await axios.delete(`/api/v0/companies/${company}/contacts/${user}`);
    },
    async setCompanyBlock(company, blocked) {
        await axios.put(`/api/v0/companies/${company}/block`, { blocked });
    },
    async setCompanyMembersSeeAll(company, membersSeeAllRequests) {
        await axios.patch(`/api/v0/companies/${company}`, { membersSeeAllRequests });
    },
    async setContactBlock(company, user, blocked) {
        await axios.put(`/api/v0/companies/${company}/contacts/${user}/block`, { blocked });
    },
    async setCompanyDomains(company, domains) {
        await axios.put(`/api/v0/companies/${company}/domains`, domains);
    },
    async getPortalSettings(project) {
        const { data } = await axios.get<ProjectPortalSettings | null>(
            `/api/v0/projects/${project}/portal`
        );
        return data;
    },
    async putPortalSettings(project, settings) {
        const { data } = await axios.put<ProjectPortalSettings>(
            `/api/v0/projects/${project}/portal`,
            settings
        );
        return data;
    },
    async getCannedResponses(project) {
        const { data } = await axios.get<CannedResponse[]>(
            `/api/v0/projects/${project}/canned-responses`
        );
        return data;
    },
    async createCannedResponse(project, name, body) {
        const { data } = await axios.post<CannedResponse>(
            `/api/v0/projects/${project}/canned-responses`,
            { name, body }
        );
        return data;
    },
    async updateCannedResponse(project, response, name, body) {
        await axios.put(`/api/v0/projects/${project}/canned-responses/${response}`, {
            name,
            body,
        });
    },
    async deleteCannedResponse(project, response) {
        await axios.delete(`/api/v0/projects/${project}/canned-responses/${response}`);
    },
};
